/**
 * Marquee - simple horizontal scrolling text strip.
 *
 * Uses CSS @keyframes + transform: translateX for GPU-accelerated motion.
 * Text is set via textContent ONLY (never innerHTML) for XSS protection — the
 * marquee renders user-submitted strings from the king-of-the-hill API.
 *
 * Speed is fixed at MARQUEE_SPEED_PX_PER_SEC. Animation duration recalculated
 * on every setText() based on text width so longer messages scroll at the same
 * pixels-per-second rate.
 */

export const MARQUEE_SPEED_PX_PER_SEC = 60

const STYLE_ID = 'star-snake-marquee-style'
const ANIM_NAME = 'star-snake-marquee-scroll'

function ensureStyles(): void {
  if (typeof document === 'undefined') return
  if (document.getElementById(STYLE_ID)) return

  const style = document.createElement('style')
  style.id = STYLE_ID
  style.textContent = `
    @keyframes ${ANIM_NAME} {
      from { transform: translateX(100%); }
      to   { transform: translateX(-100%); }
    }
    .star-snake-marquee {
      position: relative;
      display: block;
      width: 100%;
      overflow: hidden;
      box-sizing: border-box;
      white-space: nowrap;
      color: #fff7ed;
    }
    .star-snake-marquee__inner {
      display: inline-block;
      will-change: transform;
      padding-left: 100%;
      animation-name: ${ANIM_NAME};
      animation-timing-function: linear;
      animation-iteration-count: infinite;
    }
  `.trim()
  document.head.appendChild(style)
}

export class Marquee {
  private container: HTMLDivElement
  private inner: HTMLSpanElement
  private mounted = false
  private currentText = ''

  constructor() {
    ensureStyles()

    this.container = document.createElement('div')
    this.container.className = 'star-snake-marquee'
    this.container.style.cssText = `
      display: none;
      padding: 8px 0;
      border-radius: 12px;
      background: rgba(0,2,10,0.55);
      border: 1px solid rgba(251,191,36,0.25);
      box-shadow: 0 0 16px rgba(251,191,36,0.18);
      font-family: 'Satoshi', 'Inter', sans-serif;
      font-size: 0.9rem;
      letter-spacing: 0.02em;
    `

    this.inner = document.createElement('span')
    this.inner.className = 'star-snake-marquee__inner'
    this.container.appendChild(this.inner)
  }

  /**
   * Mount the marquee under the given parent element. Idempotent.
   */
  mount(parent: HTMLElement): void {
    if (this.mounted && this.container.parentElement === parent) return
    parent.appendChild(this.container)
    this.mounted = true
    // Recalculate duration in case width is now known.
    this.refreshAnimation()
  }

  /**
   * Set the text to scroll. ALWAYS uses textContent — never innerHTML — to
   * prevent XSS when displaying user-submitted king messages.
   * If text is empty or null, the marquee hides itself entirely.
   */
  setText(text: string | null | undefined): void {
    const safe = typeof text === 'string' ? text : ''
    this.currentText = safe
    this.inner.textContent = safe
    if (!safe) {
      this.container.style.display = 'none'
      return
    }
    this.container.style.display = 'block'
    this.refreshAnimation()
  }

  /**
   * Remove from the DOM and clear references.
   */
  destroy(): void {
    this.inner.textContent = ''
    if (this.container.parentElement) {
      this.container.parentElement.removeChild(this.container)
    }
    this.mounted = false
    this.currentText = ''
  }

  /**
   * Get the root element (for direct style overrides if a caller needs them).
   */
  getElement(): HTMLDivElement {
    return this.container
  }

  private refreshAnimation(): void {
    if (!this.currentText) return
    // We need a non-zero width to compute a duration. If not mounted yet, fall
    // back to a sensible default; mount() will call this again once attached.
    const containerWidth = this.container.clientWidth || 320
    // Distance travelled is approximately 2x container width because the inner
    // element scrolls from 100% to -100%.
    const distance = containerWidth * 2
    const durationSec = Math.max(6, distance / MARQUEE_SPEED_PX_PER_SEC)
    this.inner.style.animationDuration = `${durationSec.toFixed(2)}s`
  }
}
